"use client";

import React, { useState } from "react";
import { SlidersHorizontal, CircleCheck, Ruler, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface SpecDetail {
  label: Record<Lang, string>;
  value: Record<Lang, string>;
}
interface SpecCategory {
  id: number;
  title: Record<Lang, string>;
  metric: Record<Lang, string>;
  summary: Record<Lang, string>;
  details: SpecDetail[];
}
const specCategories: SpecCategory[] = [{
  id: 0,
  title: {
    en: "Geometric Dimensions",
    zh: "几何尺寸",
    es: "Dimensiones geométricas",
    de: "Geometrische Abmessungen",
    fr: "Dimensions géométriques",
    pt: "Dimensões geométricas"
  },
  metric: {
    en: "Aperture & wire diameter",
    zh: "孔径与丝径规格",
    es: "Apertura y diámetro del alambre",
    de: "Maschenweite und Drahtdurchmesser",
    fr: "Ouverture et diamètre du fil",
    pt: "Abertura e diâmetro do arame"
  },
  summary: {
    en: "Exact square aperture or micron rating paired with warp and weft wire tolerances.",
    zh: "精确方孔尺寸或微米级过滤精度，严格控制经纬丝径与公差范围。",
    es: "Apertura cuadrada exacta o micraje nominal junto con tolerancias de alambre de urdimbre y trama.",
    de: "Exakte quadratische Maschenweite oder Mikron-Filterfeinheit kombiniert mit Kett- und Schussdrahttoleranzen.",
    fr: "Ouverture carrée exacte ou seuil en microns associé aux tolérances des fils de chaîne et de trame.",
    pt: "Abertura quadrada exata ou classificação em mícrons combinada com tolerâncias de arame de urdume e trama."
  },
  details: [{
    label: {
      en: "Aperture range",
      zh: "孔径范围",
      es: "Rango de apertura",
      de: "Maschenweitenbereich",
      fr: "Plage d'ouverture",
      pt: "Faixa de abertura"
    },
    value: {
      en: "1 µm to 25 mm",
      zh: "1 µm 至 25 mm",
      es: "1 µm a 25 mm",
      de: "1 µm bis 25 mm",
      fr: "1 µm à 25 mm",
      pt: "1 µm a 25 mm"
    }
  }, {
    label: {
      en: "Mesh count",
      zh: "目数范围",
      es: "Número de mallas",
      de: "Maschenzahl",
      fr: "Nombre de mailles",
      pt: "Contagem de malha"
    },
    value: {
      en: "1 to 635 mesh/inch",
      zh: "1 至 635 目/英寸",
      es: "1 a 635 mallas/pulgada",
      de: "1 bis 635 Mesh/Zoll",
      fr: "1 à 635 mailles/pouce",
      pt: "1 a 635 malhas/polegada"
    }
  }, {
    label: {
      en: "Tolerances",
      zh: "公差标准",
      es: "Tolerancias",
      de: "Toleranzen",
      fr: "Tolérances",
      pt: "Tolerâncias"
    },
    value: {
      en: "ASTM E2016 / ISO 9044",
      zh: "ASTM E2016 / ISO 9044",
      es: "ASTM E2016 / ISO 9044",
      de: "ASTM E2016 / ISO 9044",
      fr: "ASTM E2016 / ISO 9044",
      pt: "ASTM E2016 / ISO 9044"
    }
  }, {
    label: {
      en: "Open area ratio",
      zh: "开孔率",
      es: "Área abierta",
      de: "Offene Siebfläche",
      fr: "Surface ouverte",
      pt: "Área aberta"
    },
    value: {
      en: "18% – 72%",
      zh: "18% – 72%",
      es: "18% – 72%",
      de: "18% – 72%",
      fr: "18% – 72%",
      pt: "18% – 72%"
    }
  }]
}, {
  id: 1,
  title: {
    en: "Weave Structure",
    zh: "编织结构",
    es: "Estructura de tejido",
    de: "Webstruktur",
    fr: "Structure de tissage",
    pt: "Estrutura de tecelagem"
  },
  metric: {
    en: "Plain, twill & dutch weaves",
    zh: "平纹、斜纹与密纹编织",
    es: "Tejidos liso, sarga y Dutch",
    de: "Leinwand-, Köper- und Tressengewebe",
    fr: "Tissages uni, sergé et hollandais",
    pt: "Tramas lisa, sarja e holandesa"
  },
  summary: {
    en: "Mechanical stability tailored to solid retention, high backwash cycles, or low delta-P.",
    zh: "针对颗粒截留、高频反冲洗或低压差要求量身定制的高机械稳定性结构。",
    es: "Estabilidad mecánica adaptada a la retención de sólidos, ciclos intensos de retrolavado o baja delta-P.",
    de: "Mechanische Stabilität ausgelegt auf Feststoffrückhaltung, häufige Rückspülzyklen oder geringen Differenzdruck.",
    fr: "Stabilité mécanique adaptée à la rétention de solides, aux contre-lavages fréquents ou aux faibles pertes de charge.",
    pt: "Estabilidade mecânica sob medida para retenção de sólidos, ciclos intensos de retrolavagem ou baixa perda de carga."
  },
  details: [{
    label: {
      en: "Primary weaves",
      zh: "常规编织类型",
      es: "Tejidos principales",
      de: "Hauptbindungsarten",
      fr: "Tissages principaux",
      pt: "Tramas principais"
    },
    value: {
      en: "Plain, Twill, Plain Dutch",
      zh: "平纹、斜纹、平纹密纹",
      es: "Liso, Sarga, Dutch liso",
      de: "Leinwand, Köper, Glatte Tresse",
      fr: "Uni, Sergé, Hollandais uni",
      pt: "Liso, Sarja, Holandês liso"
    }
  }, {
    label: {
      en: "High-pressure weaves",
      zh: "高压承载编织",
      es: "Tejidos de alta presión",
      de: "Hochdruck-Webarten",
      fr: "Tissages haute pression",
      pt: "Tramas de alta pressão"
    },
    value: {
      en: "Twill Dutch, Reverse Dutch",
      zh: "斜纹密纹、反向密纹",
      es: "Dutch cruzado, Dutch inverso",
      de: "Köpertresse, Panzertresse",
      fr: "Hollandais sergé, Hollandais inversé",
      pt: "Holandês sarja, Holandês invertido"
    }
  }, {
    label: {
      en: "Flow characteristics",
      zh: "流动特性",
      es: "Características de flujo",
      de: "Strömungsverhalten",
      fr: "Caractéristiques d'écoulement",
      pt: "Características de fluxo"
    },
    value: {
      en: "Surface vs. depth filtration",
      zh: "表层截留 vs. 深层过滤",
      es: "Filtración superficial vs. profundidad",
      de: "Oberflächen- vs. Tiefenfiltration",
      fr: "Filtration de surface vs. en profondeur",
      pt: "Filtração de superfície vs. profundidade"
    }
  }, {
    label: {
      en: "Stability rating",
      zh: "结构稳定性",
      es: "Clasificación de estabilidad",
      de: "Formstabilität",
      fr: "Indice de stabilité",
      pt: "Índice de estabilidade"
    },
    value: {
      en: "Zero wire shifting",
      zh: "零丝线位移",
      es: "Cero desplazamiento de hilos",
      de: "Keine Drahtverschiebung",
      fr: "Zéro déplacement de fils",
      pt: "Sem deslocamento de fios"
    }
  }]
}, {
  id: 2,
  title: {
    en: "Material Metallurgy",
    zh: "材质与冶金特性",
    es: "Metalurgia de materiales",
    de: "Werkstoffmetallurgie",
    fr: "Métallurgie des matériaux",
    pt: "Metalurgia dos materiais"
  },
  metric: {
    en: "AISI 304, 304L, 316, 316L",
    zh: "AISI 304、304L、316、316L",
    es: "AISI 304, 304L, 316, 316L",
    de: "AISI 304, 304L, 316, 316L",
    fr: "AISI 304, 304L, 316, 316L",
    pt: "AISI 304, 304L, 316, 316L"
  },
  summary: {
    en: "Alloy chemistry for extreme temperature, acid, or chloride exposure available with order-specified testing.",
    zh: "适用于极温、强酸或高氯化物腐蚀工况的合金化学成分，可按订单要求提供检验。",
    es: "Química de aleación para exposición a temperaturas extremas, ácidos o cloruros con ensayos según pedido.",
    de: "Legierungschemie für extreme Temperaturen, Säuren oder Chloride, mit auftragsbezogener Prüfung lieferbar.",
    fr: "Composition d'alliage pour températures extrêmes, acides ou chlorures avec contrôle sur demande à la commande.",
    pt: "Química de liga para temperaturas extremas, ácidos ou cloretos com testes especificados no pedido."
  },
  details: [{
    label: {
      en: "Standard grades",
      zh: "标准不锈钢牌号",
      es: "Calidades estándar",
      de: "Standardgüten",
      fr: "Nuances standard",
      pt: "Graus padrão"
    },
    value: {
      en: "SS 304, 304L, 316, 316L",
      zh: "SS 304, 304L, 316, 316L",
      es: "SS 304, 304L, 316, 316L",
      de: "SS 304, 304L, 316, 316L",
      fr: "SS 304, 304L, 316, 316L",
      pt: "SS 304, 304L, 316, 316L"
    }
  }, {
    label: {
      en: "High-temperature wire",
      zh: "耐热合金丝材",
      es: "Alambre para alta temperatura",
      de: "Hochtemperaturdraht",
      fr: "Fil haute température",
      pt: "Arame para alta temperatura"
    },
    value: {
      en: "AISI 314, AISI 310S",
      zh: "AISI 314, AISI 310S",
      es: "AISI 314, AISI 310S",
      de: "AISI 314, AISI 310S",
      fr: "AISI 314, AISI 310S",
      pt: "AISI 314, AISI 310S"
    }
  }, {
    label: {
      en: "Verification",
      zh: "材质验证",
      es: "Verificación",
      de: "Prüfnachweise",
      fr: "Contrôles",
      pt: "Verificação"
    },
    value: {
      en: "PMI test & MTR (upon request)",
      zh: "PMI 光谱检测与材质单 (按订单要求)",
      es: "Ensayos PMI y MTR (bajo pedido)",
      de: "PMI-Prüfung & Werkszeugnis (auf Anfrage)",
      fr: "Contrôle PMI et CCPU (sur demande)",
      pt: "Teste PMI e certificado MTR (sob pedido)"
    }
  }]
}];
const contentCopy = {
  badge: {
    en: "Engineering Limits & Tolerances",
    zh: "工程极限与公差",
    es: "Límites y tolerancias técnicas",
    de: "Fertigungsgrenzen & Toleranzen",
    fr: "Limites d'ingénierie & tolérances",
    pt: "Limites de engenharia e tolerâncias"
  },
  h2: {
    en: "Core Filter Mesh Engineering Parameters",
    zh: "核心过滤丝网工程技术指标",
    es: "Parámetros técnicos clave de la malla de filtración",
    de: "Wesentliche Filtergewebe-Engineeringparameter",
    fr: "Paramètres clés d'ingénierie des toiles de filtration",
    pt: "Parâmetros técnicos principais da malha de filtração"
  },
  lead: {
    en: "Explore dimensional ranges, weave geometries, and metallurgical properties engineered for industrial solid-liquid separation and gas filtration.",
    zh: "深入了解面向工业固液分离与气体过滤工况的尺寸范围、编织几何结构与冶金材料特性。",
    es: "Explore los rangos dimensionales, geometrías de tejido y propiedades metalúrgicas para filtración industrial.",
    de: "Entdecken Sie Maßbereiche, Webgeometrien und metallurgische Eigenschaften für industrielle Filtrationssysteme.",
    fr: "Découvrez les plages dimensionnelles, géométries de tissage et propriétés métallurgiques pour la filtration industrielle.",
    pt: "Explore faixas dimensionais, geometrias de trama e propriedades metalúrgicas para filtração industrial."
  },
  cardTitle: {
    en: "Key Spec Parameters",
    zh: "核心技术参数",
    es: "Parámetros técnicos clave",
    de: "Wichtigste Kennwerte",
    fr: "Paramètres techniques clés",
    pt: "Parâmetros técnicos principais"
  },
  cardTooltip: {
    en: "Select a parameter group to inspect standard engineering limits.",
    zh: "选择参数类别以查看标准工程极限与规格。",
    es: "Seleccione un grupo de parámetros para revisar límites y tolerancias.",
    de: "Wählen Sie eine Parametergruppe, um Fertigungsgrenzen einzusehen.",
    fr: "Sélectionnez un groupe de paramètres pour vérifier les limites de fabrication.",
    pt: "Selecione um grupo de parâmetros para verificar limites de fabricação."
  },
  cardDesc: {
    en: "Review primary variables required for quotation and custom mesh weaving.",
    zh: "查看报价核算与定制丝网编织所需的核心参数指标。",
    es: "Revise las variables requeridas para presupuestos y tejido de malla a medida.",
    de: "Prüfen Sie die wesentlichen Variablen für Kalkulation und Webung nach Maß.",
    fr: "Consultez les variables requises pour le devis et le tissage sur mesure.",
    pt: "Analise as variáveis necessárias para cotação e tecelagem personalizada."
  },
  customSpecsTitle: {
    en: "Custom specs supported",
    zh: "支持客制化工程定制",
    es: "Ingeniería personalizada",
    de: "Kundenspezifische Maße",
    fr: "Fabrication sur mesure",
    pt: "Engenharia sob medida"
  },
  customSpecsSub: {
    en: "Slit coils, stamped discs, welded cylinders",
    zh: "精密分条卷带、冲压滤片、焊接滤筒滤管",
    es: "Bobinas flejadas, discos estampados, cilindros soldados",
    de: "Spaltbänder, Stanzronden, geschweißte Filterzylinder",
    fr: "Bandes refendues, disques découpés, cylindres soudés",
    pt: "Bobinas fendidas, discos estampados, cilindros soldados"
  }
};
export default function MeshSpec() {
  const {
    lang
  } = useLanguage();
  const [selectedSpec, setSelectedSpec] = useState<number>(0);
  return <section id="filtermeshspeccontent" data-nav="light" className="relative w-full overflow-hidden bg-background mf-section border-b border-border text-foreground">
      <div className="mf-container">
        <Reveal>
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted text-foreground font-mono mf-caption mb-3 border border-border">
              <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
              <span>{pick(lang, contentCopy.badge)}</span>
            </div>
            <h2 className="font-default mf-h2 text-foreground tracking-tight">
              {pick(lang, contentCopy.h2)}
            </h2>
            <p className="mt-3 text-muted-foreground mf-lead">
              {pick(lang, contentCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Specification Parameter Console */}
        <Reveal delay={100}>
          <Card className="border border-border bg-card overflow-hidden max-w-4xl">
            <CardHeader className="bg-muted/40 pb-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="size-4 text-primary" aria-hidden="true" />
                  <CardTitle className="text-sm font-semibold tracking-tight text-foreground font-mono">
                    {pick(lang, contentCopy.cardTitle)}
                  </CardTitle>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground hover:text-foreground">
                      <Info className="size-4" aria-hidden="true" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {pick(lang, contentCopy.cardTooltip)}
                  </TooltipContent>
                </Tooltip>
              </div>
              <CardDescription className="mf-caption text-muted-foreground">
                {pick(lang, contentCopy.cardDesc)}
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0">
              {/* Parameter Switcher Tabs */}
              <div className="grid grid-cols-3 border-b border-border bg-muted/30 text-center">
                {specCategories.map(cat => {
                const isActive = selectedSpec === cat.id;
                return <button key={cat.id} type="button" onClick={() => setSelectedSpec(cat.id)} className={`py-2.5 px-2 mf-caption font-medium cursor-pointer transition-colors border-r last:border-r-0 border-border select-none ${isActive ? "bg-card text-foreground font-semibold border-b-2 border-b-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"}`}>
                      {pick(lang, cat.title)}
                    </button>;
              })}
              </div>

              {/* Active Spec Content */}
              <div className="p-5 sm:p-6 space-y-5">
                <div>
                  <div className="flex items-center gap-1.5 mf-caption font-mono font-medium text-primary mb-1">
                    <CircleCheck className="size-3.5" aria-hidden="true" />
                    <span>{pick(lang, specCategories[selectedSpec].metric)}</span>
                  </div>
                  <p className="mf-caption text-muted-foreground leading-normal">
                    {pick(lang, specCategories[selectedSpec].summary)}
                  </p>
                </div>

                <Separator className="bg-border/70" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {specCategories[selectedSpec].details.map((detail, idx) => <div key={idx} className="bg-muted/40 border border-border rounded-md p-3">
                      <span className="block mf-caption font-mono text-muted-foreground">
                        {pick(lang, detail.label)}
                      </span>
                      <span className="block mf-caption font-semibold text-foreground mt-0.5">
                        {pick(lang, detail.value)}
                      </span>
                    </div>)}
                </div>

                {/* Technical Blueprint Visual Micro-block */}
                <div className="mt-4 rounded-md border border-dashed border-border bg-muted/20 p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                      <Ruler className="size-5" aria-hidden="true" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-medium text-foreground block">
                        {pick(lang, contentCopy.customSpecsTitle)}
                      </span>
                      <span className="mf-caption text-muted-foreground block">
                        {pick(lang, contentCopy.customSpecsSub)}
                      </span>
                    </div>
                  </div>
                  <Button asChild variant="ghost" size="sm" className="h-8 mf-caption px-3 text-primary hover:text-primary self-start sm:self-auto">
                    <Link to="/OEMCustomMeshEngineeringPage">
                      {pick(lang, COMMON.viewDetails)}
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </Reveal>
      </div>
    </section>;
}